import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { TrendUp, ChartBar, Fire, Link as LinkIcon, Cursor, MapPin, UsersThree, CalendarCheck, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";

const platformColors = {
  facebook: "#1877F2", instagram: "#E1306C", linkedin: "#0A66C2", tiktok: "#010101", youtube: "#FF0000", newsletter: "#5FA8B4",
};

const tooltipStyleShared = { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", color: "#3C4048" };

function ClickLocationsSection({ linkAnalytics }) {
  if (!linkAnalytics || (!linkAnalytics.top_countries?.length && !linkAnalytics.top_cities?.length)) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="click-locations-section">
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
        <h2 className="text-xl font-bold text-[#3C4048] mb-4 flex items-center gap-2" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          <MapPin size={18} weight="fill" style={{ color: "#3A7A8C" }} /> Clicks by Country
        </h2>
        <div className="space-y-2">
          {linkAnalytics.top_countries.map((c, i) => (
            <div key={i} data-testid={`click-country-${i}`} className="flex items-center justify-between text-sm">
              <span className="text-[#3C4048]">{c.name}</span>
              <span className="text-[#15803d] font-bold">{c.clicks}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
        <h2 className="text-xl font-bold text-[#3C4048] mb-4 flex items-center gap-2" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          <MapPin size={18} weight="fill" style={{ color: "#5FA8B4" }} /> Clicks by City
        </h2>
        <div className="space-y-2">
          {linkAnalytics.top_cities.map((c, i) => (
            <div key={i} data-testid={`click-city-${i}`} className="flex items-center justify-between text-sm">
              <span className="text-[#3C4048]">{c.name}</span>
              <span className="text-[#15803d] font-bold">{c.clicks}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AudienceSection({ audience }) {
  if (!audience) return null;
  if (!audience.available) {
    return (
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="audience-unavailable">
        <h2 className="text-xl font-bold text-[#3C4048] mb-2 flex items-center gap-2" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          <UsersThree size={18} weight="fill" style={{ color: "#3A7A8C" }} /> Audience Demographics
        </h2>
        <p className="text-[#7A9090] text-sm flex items-center gap-1.5"><WarningCircle size={14} /> {audience.message || "Not available yet."}</p>
      </div>
    );
  }
  const genderData = (audience.gender || []).map((g) => ({ name: g.key, value: g.value }));
  const ageData = (audience.age || []).map((a) => ({ name: a.key, value: a.value }));
  return (
    <div className="space-y-4" data-testid="audience-section">
      <h2 className="text-xl font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
        Audience Demographics <span className="text-[#7A9090] text-xs font-normal">(Instagram followers — aggregate, not per-click)</span>
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h3 className="text-sm font-bold text-[#3C4048] mb-4">By Age</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#7A9090" tick={{ fontSize: 11 }} />
              <YAxis stroke="#7A9090" allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyleShared} />
              <Bar dataKey="value" fill="#5FA8B4" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h3 className="text-sm font-bold text-[#3C4048] mb-4">By Gender</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={genderData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                {genderData.map((entry, index) => <Cell key={index} fill={["#3A7A8C", "#E1306C", "#5FA8B4"][index % 3]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyleShared} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function BookingsSection({ bookings }) {
  if (!bookings || !bookings.configured) return null;
  return (
    <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-4" data-testid="bookings-section">
      <h2 className="text-xl font-bold text-[#3C4048] flex items-center gap-2" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
        <CalendarCheck size={18} weight="fill" style={{ color: "#15803d" }} /> Bookings from Social
      </h2>
      {bookings.total_bookings === 0 ? (
        <p className="text-[#7A9090] text-sm">No bookings received via the Pabau webhook yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-[#7A9090] text-xs">Total bookings</p>
              <p className="text-2xl font-bold text-[#3C4048]" data-testid="bookings-total">{bookings.total_bookings}</p>
            </div>
            <div>
              <p className="text-[#7A9090] text-xs">Directly attributed</p>
              <p className="text-2xl font-bold text-[#15803d]" data-testid="bookings-direct">{bookings.direct_attributed}</p>
            </div>
            <div>
              <p className="text-[#7A9090] text-xs">Loosely correlated</p>
              <p className="text-2xl font-bold text-[#a16207]" data-testid="bookings-correlated">{bookings.correlated_only}</p>
            </div>
          </div>
          <p className="text-[#7A9090] text-xs">
            "Directly attributed" means Pabau passed along a source tag we recognized. "Loosely correlated" just means
            social clicks happened in the 48h before the booking — not a guaranteed match.
          </p>
        </>
      )}
    </div>
  );
}

function LinkClicksSection({ linkAnalytics }) {
  if (!linkAnalytics || linkAnalytics.total_links === 0) return null;
  const platformData = Object.entries(linkAnalytics.by_platform).map(([source, clicks]) => ({ name: source, clicks }));
  return (
    <div className="space-y-6" data-testid="link-clicks-section">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="analytics-total-clicks">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#7A9090] text-sm">Total Link Clicks</span>
            <Cursor size={20} weight="duotone" style={{ color: "#3A7A8C" }} />
          </div>
          <div className="text-3xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800, color: "#3A7A8C" }}>{linkAnalytics.total_clicks}</div>
        </div>
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="analytics-total-tracked-links">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#7A9090] text-sm">Tracked Links</span>
            <LinkIcon size={20} weight="duotone" style={{ color: "#15803d" }} />
          </div>
          <div className="text-3xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800, color: "#15803d" }}>{linkAnalytics.total_links}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h2 className="text-xl font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Clicks by Platform</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={platformData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#7A9090" />
              <YAxis stroke="#7A9090" allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyleShared} cursor={{ fill: "#f0f7f8" }} />
              <Bar dataKey="clicks" radius={[6, 6, 0, 0]}>
                {platformData.map((entry, index) => <Cell key={`cell-${index}`} fill={platformColors[entry.name] || "#5FA8B4"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h2 className="text-xl font-bold text-[#3C4048] mb-1" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Clicks Over Time (30 days)</h2>
          <p className="text-[#7A9090] text-xs mb-3">Only tracks our own built-in fallback links — Short.io reports lifetime totals, not a daily log, so branded-domain links may not appear here even though their total is counted above.</p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={linkAnalytics.timeseries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#7A9090" tick={{ fontSize: 11 }} />
              <YAxis stroke="#7A9090" allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyleShared} />
              <Line type="monotone" dataKey="clicks" stroke="#5FA8B4" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {linkAnalytics.top_links.length > 0 && (
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid="top-links-list">
          <h2 className="text-xl font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Top Performing Links</h2>
          <div className="space-y-3">
            {linkAnalytics.top_links.map((link, index) => (
              <div key={link.id} data-testid={`top-link-${index}`} className="bg-[#fafafa] border border-[#e5e7eb] rounded-[12px] p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[link.source] || "#7A9090" }}>{link.source}</span>
                    <span className="text-[#7A9090] text-xs">campaign: {link.campaign}</span>
                  </div>
                  <p className="text-[#3C4048] text-sm truncate">{link.url}</p>
                </div>
                <span className="text-[#15803d] text-lg font-bold shrink-0">{link.clicks}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <ClickLocationsSection linkAnalytics={linkAnalytics} />
    </div>
  );
}

export default function Analytics() {
  const api = useApi();
  const [analytics, setAnalytics] = useState(null);
  const [linkAnalytics, setLinkAnalytics] = useState(null);
  const [audience, setAudience] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchLinkAnalytics();
    fetchAudience();
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get(`/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkAnalytics = async () => {
    try {
      const response = await api.get(`/analytics/links`);
      setLinkAnalytics(response.data);
    } catch (error) { /* Admin-only endpoint — non-admins simply won't see this section */ }
  };

  const fetchAudience = async () => {
    try {
      const response = await api.get(`/analytics/audience`);
      setAudience(response.data);
    } catch (error) { /* Admin-only endpoint */ }
  };

  const fetchBookings = async () => {
    try {
      const response = await api.get(`/analytics/bookings`);
      setBookings(response.data);
    } catch (error) { /* Admin-only endpoint */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#3C4048] text-xl">Loading analytics…</div>
      </div>
    );
  }

  if (!analytics || analytics.total_posts === 0) {
    return (
      <div className="space-y-8" data-testid="analytics-container">
        <div>
          <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
            Analytics Dashboard
          </h1>
          <p className="text-[#7A9090]">No analytics data available yet</p>
        </div>
        {linkAnalytics && linkAnalytics.total_links > 0 ? (
          <LinkClicksSection linkAnalytics={linkAnalytics} />
        ) : (
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-12 text-center">
            <p className="text-[#7A9090]">Posts need to be published and have analytics data to appear here</p>
          </div>
        )}
        <AudienceSection audience={audience} />
        <BookingsSection bookings={bookings} />
      </div>
    );
  }

  const platformData = Object.entries(analytics.platform_breakdown).map(([platform, data]) => ({
    name: platform,
    posts: data.count,
    engagement: data.total_engagement / data.count || 0,
  }));

  const tooltipStyle = { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", color: "#3C4048" };

  const statCards = [
    { label: "Total Posts", value: analytics.total_posts, icon: ChartBar, color: "#3A7A8C", testid: "analytics-total-posts" },
    { label: "Avg Engagement Rate", value: `${analytics.avg_engagement_rate}%`, icon: TrendUp, color: "#15803d", testid: "analytics-avg-engagement" },
    { label: "Top Performers", value: analytics.top_performing_posts.length, icon: Fire, color: "#ef4444", testid: "analytics-top-posts" },
  ];

  return (
    <div className="space-y-8" data-testid="analytics-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
          Analytics Dashboard
        </h1>
        <p className="text-[#7A9090]">Performance insights and engagement metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white border border-[#e5e7eb] rounded-[12px] p-6" data-testid={c.testid}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#7A9090] text-sm">{c.label}</span>
                <Icon size={20} weight="duotone" style={{ color: c.color }} />
              </div>
              <div className="text-3xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800, color: c.color }}>{c.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h2 className="text-xl font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Platform Performance</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platformData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#7A9090" />
              <YAxis stroke="#7A9090" />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#f0f7f8" }} />
              <Bar dataKey="posts" fill="#5FA8B4" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h2 className="text-xl font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Platform Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={platformData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={100} dataKey="posts">
                {platformData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={platformColors[entry.name] || "#5FA8B4"} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {analytics.top_performing_posts.length > 0 && (
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h2 className="text-xl font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Top Performing Posts</h2>
          <div className="space-y-3">
            {analytics.top_performing_posts.map((post, index) => (
              <div key={post.id} data-testid={`top-post-${index}`} className="bg-[#fafafa] border border-[#e5e7eb] rounded-[12px] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[post.platform] || "#7A9090" }}>{post.platform}</span>
                  <span className="text-[#15803d] text-sm font-bold">{post.analytics_data?.engagement_rate}% engagement</span>
                  {post.analytics_data?.is_evergreen && (
                    <span className="px-2 py-1 bg-[#f0f7f8] text-[#3A7A8C] text-xs rounded font-semibold">EVERGREEN</span>
                  )}
                </div>
                <p className="text-[#3C4048] text-sm line-clamp-2 whitespace-pre-line">{post.final_text || post.draft_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <LinkClicksSection linkAnalytics={linkAnalytics} />
      <AudienceSection audience={audience} />
      <BookingsSection bookings={bookings} />
    </div>
  );
}
